import { Button } from '@/components/ui/button';
import type { UploadItem } from '../hooks/useFileUploads';

/** How many failed names are listed before the rest are counted instead. */
const NAMED_FAILURES = 5;

/**
 * What the uploads are doing, while they do it.
 *
 * Uploading used to report only failure, and only as a toast: dropping a
 * folder of two hundred files showed nothing at all until something broke,
 * including the long stretch where the dropped tree is being walked and no
 * request has gone out yet. Forty failures also meant forty toasts.
 *
 * Deliberately no progress bar — the brand reserves that device for two
 * specific in-product uses (docs/brand.md FD-16), and for a batch of files a
 * count says more than a bar anyway.
 */
export function UploadTray({
  isCollecting,
  items,
  onRetryFailed,
  onDismiss,
}: {
  isCollecting: boolean;
  items: UploadItem[];
  onRetryFailed: () => void;
  onDismiss: () => void;
}) {
  if (!isCollecting && items.length === 0) return null;

  const done = items.filter((item) => item.status === 'done').length;
  const failed = items.filter((item) => item.status === 'failed');
  const active = items.filter((item) => item.status === 'uploading').length;
  const finished = !isCollecting && active === 0;

  return (
    <div
      aria-live="polite"
      className="border-border bg-card flex flex-none items-start justify-between gap-3 rounded-lg border px-3 py-2 text-sm"
    >
      <div className="flex min-w-0 flex-col gap-1">
        <p className="text-foreground">
          {isCollecting
            ? 'Reading the dropped folder…'
            : finished
              ? failed.length === 0
                ? `${String(done)} ${done === 1 ? 'file' : 'files'} uploaded`
                : `${String(done)} uploaded, ${String(failed.length)} failed`
              : `Uploading ${String(done)} of ${String(items.length)}`}
        </p>
        {failed.length > 0 && (
          <ul className="text-muted-foreground flex flex-col text-xs">
            {failed.slice(0, NAMED_FAILURES).map((item) => (
              <li key={item.id} className="truncate">
                {item.name}
              </li>
            ))}
            {failed.length > NAMED_FAILURES && (
              <li>and {failed.length - NAMED_FAILURES} more</li>
            )}
          </ul>
        )}
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {finished && failed.length > 0 && (
          <Button variant="outline" size="sm" onClick={onRetryFailed}>
            Retry failed
          </Button>
        )}
        {finished && (
          <Button variant="ghost" size="sm" onClick={onDismiss}>
            Dismiss
          </Button>
        )}
      </div>
    </div>
  );
}
